import createCache from "@emotion/cache";

/**
 * Opens a separate OS-level window the user can move to another monitor.
 *
 * Must be called from a user gesture — `window.open` requires transient activation.
 */

type EmotionCache = ReturnType<typeof createCache>;

export type Popout = {
  win: Window;
  cache: EmotionCache;
};

const COPYABLE_STYLES = 'link[rel="stylesheet"], style';

/** Relative refs only — leaves data:, blob:, scheme: and //host alone. */
const CSS_URL =
  /url\(\s*(['"]?)(?!['"]?(?:[a-z][a-z0-9+.-]*:|\/\/))([^'")]+)\1\s*\)/gi;

/**
 * CSS text for a copied style element. Exported for tests.
 *
 * Emotion's production "speedy" mode inserts rules through the CSSOM and leaves the
 * element's text empty, so cloning copies nothing — rebuild from `cssRules`. Relative
 * `url(...)` is absolutised because a copied sheet resolves it against the popout, not
 * its origin sheet; that's what drops @font-face and leaves fallback text showing.
 */
export function styleTextFrom(el: HTMLStyleElement): string {
  let text = el.textContent ?? "";
  if (!text) {
    try {
      text = Array.from(el.sheet?.cssRules ?? [])
        .map((rule) => rule.cssText)
        .join("\n");
    } catch {
      return ""; // cross-origin sheet; nothing readable to copy
    }
  }
  const base = el.sheet?.href ?? document.baseURI;
  return text.replace(CSS_URL, (whole, _quote, ref: string) => {
    try {
      return `url("${new URL(ref, base).href}")`;
    } catch {
      return whole;
    }
  });
}

/** Give the fresh window a head/body, base styling, and the opener's stylesheets. */
function primeDocument(win: Window, title: string) {
  const doc = win.document;

  // Defensive: an about:blank document normally has both already.
  if (!doc.head) {
    doc.documentElement.appendChild(doc.createElement("head"));
  }
  if (!doc.body) {
    doc.documentElement.appendChild(doc.createElement("body"));
  }

  doc.title = title;

  // Required: a window.open("") popup is at about:blank, where relative asset
  // URLs (fonts, images, icons) would 404.
  const base = doc.createElement("base");
  base.setAttribute("href", document.baseURI);
  doc.head.appendChild(base);

  // Resets, fonts and already-injected styles. Emotion styles for the popped-out
  // subtree come from the returned cache instead.
  document.querySelectorAll(COPYABLE_STYLES).forEach((node) => {
    if (node.tagName !== "STYLE") {
      doc.head.appendChild(node.cloneNode(true));
      return;
    }
    const copy = doc.createElement("style");
    copy.textContent = styleTextFrom(node as HTMLStyleElement);
    doc.head.appendChild(copy);
  });

  doc.documentElement.className = document.documentElement.className;
  doc.body.className = document.body.className;
  doc.body.style.margin = "0";
  doc.body.style.background = "#ffffff";
}

export function openPopout(opts: {
  title: string;
  width?: number;
  height?: number;
}): Popout | null {
  // Never ask for more than the display can show.
  const width = Math.min(opts.width ?? 1040, window.screen.availWidth);
  const height = Math.min(opts.height ?? 900, window.screen.availHeight);

  const win = window.open("", "", `popup,width=${width},height=${height}`);
  if (!win) return null; // popup blocked

  // A blocker that hands back a window then neuters it makes these throw. Reported as
  // "couldn't open" so the caller shows its blocked message, and the husk gets closed.
  try {
    primeDocument(win, opts.title);
    const cache = createCache({ key: "pop", container: win.document.head });
    return { win, cache };
  } catch {
    try {
      win.close();
    } catch {
      /* already gone */
    }
    return null;
  }
}
